import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useVerifyExternalChargeMutation } from "../../store/api/externalChargeSlice";
import "./ReceivePayment.scss";
import { useTranslation } from "react-i18next";

const ReceivePaymentComplete = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  const referenceId = queryParams.get("referenceId");
  const resultCode = queryParams.get("resultCode");
  const hash = queryParams.get("hash");
  const orderNumber = queryParams.get("orderNumber");
  const amount = queryParams.get("amount");
  const cardType = queryParams.get("cardType");

  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role === "super_admin" ? "admin" : user?.role;

  const [status, setStatus] = useState("processing");
  const [txnInfo, setTxnInfo] = useState(null);

  const [verifyCharge] = useVerifyExternalChargeMutation();

  useEffect(() => {
    const chargeId = localStorage.getItem("external_charge_id");
    const chargeNumber = localStorage.getItem("external_charge_number");

    if (!chargeId) {
      setStatus("failed");
      return;
    }

    const doVerify = async () => {
      try {
        const res = await verifyCharge({
          external_charge_id: Number(chargeId),
          reference_id: referenceId,
          result_code: resultCode,
          hash: hash || undefined,
        }).unwrap();

        if (res.data.payment_status === "completed") {
          setStatus("success");
          setTxnInfo({
            charge_number: chargeNumber || res.data.charge_number,
            amount_cents: res.data.amount_cents,
            currency: res.data.currency,
          });
          toast.success("Payment received successfully!");
        } else {
          setStatus("failed");
        }
      } catch (err) {
        setStatus("failed");
        toast.error(err?.data?.message || "Payment verification failed.");
      } finally {
        localStorage.removeItem("external_charge_id");
        localStorage.removeItem("external_charge_number");
      }
    };

    doVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="payment-complete">
      <div className="p-0">
        {status === "processing" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon" style={{ background: "#f0fdf4" }}>
                <div className="spinner-border text-success" role="status" />
              </div>
              <h2>{t("verifyingPayment")}...</h2>
              <p>{"ConfirmTransaction"}</p>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon">
                <FaCheck />
              </div>
              <h2>{t("paymentReceived")}</h2>
              <p>{t("chargeProcessed")}</p>

              <div className="order-details">
                {txnInfo?.charge_number && (
                  <div className="detail-row">
                    <span>{t("chargeNo")}</span>
                    <span>{txnInfo.charge_number}</span>
                  </div>
                )}
                {orderNumber && (
                  <div className="detail-row">
                    <span>{t("orderId")}</span>
                    <span>{orderNumber}</span>
                  </div>
                )}
                {(amount || txnInfo?.amount_cents) && (
                  <div className="detail-row">
                    <span>{t("amountCharged")}</span>
                    <span>
                      ${((amount || txnInfo?.amount_cents) / 100).toFixed(2)}{" "}
                      {txnInfo?.currency || ""}
                    </span>
                  </div>
                )}
                {cardType && (
                  <div className="detail-row">
                    <span>{t("paymentMethod")}</span>
                    <span>{cardType}</span>
                  </div>
                )}
              </div>

              <div className="d-flex gap-2 justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(`/receive-payment-history`)}
                >
                  {t("viewHistory")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => navigate(`/receive-payment`)}
                >
                  {t("new")} {t("charge")}
                </button>
              </div>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon failed-icon">
                <FaTimes />
              </div>
              <h2>{t("paymentFailed")}</h2>
              <p>{t("paymentFailedDesc")}</p>
              {orderNumber && (
                <div className="order-details">
                  <div className="detail-row">
                    <span>{t("orderId")}</span>
                    <span>{orderNumber}</span>
                  </div>
                </div>
              )}
              <div className="d-flex gap-2 justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary btn-failed"
                  onClick={() => navigate(`/receive-payment`)}
                >
                  {t("tryAgain")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(`/dashboard`)}
                >
                  {t("dashboard")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceivePaymentComplete;
